import { Fragment, useEffect, useState } from "react";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";

import { IInvoiceItem } from "../../models/invoice/IInvoice";
import classNames from "../../utils/classNames";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";

export default function CreditInvoiceItemsTable({
  data,
  setValue,
  register,
  getValues,
  watch
}: {
  data: IInvoiceItem[],
  setValue: (name: string, value: any) => void,
  register: any,
  getValues: any,
  watch: any
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const colHelper = createColumnHelper<IInvoiceItem>();
  const [total, setTotal] = useState(0);
  const { saveHandlers } = useDefaultCRUDHandlers("Invoice Item");

  const calculateTotal = () => {
    return data.reduce((acc, item) => {
      return acc + getLineTotal(item.id);
    }, 0);
  }

  const getLineTotal = (id: string) => {
    const unitPrice = getValues(`unitPrice-${id}`) || 0;
    console.log("Unit Price for item", id, ":", unitPrice);
    const quantity = getValues(`quantity-${id}`) || 1;

    return unitPrice * quantity;
  }

  useEffect(() => {
    console.log("Data changed, recalculating total and setting default values");
    for (const item of data) {
      // Set default values for unitPrice and taxRate if they are not set
      if (item.unitPrice === undefined || item.unitPrice === null) {
        setValue(`unitPrice-${item.id}`, "0.00");
      } else {
        setValue(`unitPrice-${item.id}`, item.unitPrice.toFixed(2));
      }

      if (item.taxRate === undefined || item.taxRate === null) {
        setValue(`taxRate-${item.id}`, "0");
      } else {
        setValue(`taxRate-${item.id}`, item.taxRate.toString());
      }

      // do something with the discount
      if (item.discount === undefined || item.discount === null) {
        setValue(`discount-${item.id}`, "0");
      } else {
        setValue(`discount-${item.id}`, item.discount.toString());
      }

      setValue(`quantity-${item.id}`, item.quantity || "1");

    }

    setTotal(calculateTotal());
  }, [data]);

  const cols = [
    colHelper.accessor("description", {
      header: "Description",
      sortingFn: "auto",
    }),

    colHelper.accessor("quantity", {
      id: "itemQuantity",
      header: "Quantity",
      enableSorting: true,
      cell: (info) => <span className="flex items-center gap-x-2">
        {info.row.original.quantity}
      </span>,
    }),
    colHelper.accessor("unitPrice", {
      id: "unitPrice",
      header: "Unit Price",
      enableSorting: false,
      cell: (info) => <span className="flex items-center gap-x-2">
        £<input
          type="number"
          {...register(`unitPrice-${info.row.original.id}`)}
          className="w-24"
          min={0}
          onClick={(e) => e.stopPropagation()}
        />
      </span>,
    }),
    // colHelper.accessor("discount", {
    //   header: "Discount",
    //   sortingFn: "auto",
    // }),

    colHelper.accessor("taxRate", {
      id: "taxRate",
      header: "Tax Rate",
      enableSorting: false,
      cell: (info) => <span className="flex items-center gap-x-2">
        <input
          type="number"
          {...register(`taxRate-${info.row.original.id}`)}
          className="w-16"
          min={0}
          onClick={(e) => e.stopPropagation()}
        />%
      </span>,
    }),

    // -------- CREDIT -------- //
    colHelper.display({
      id: "credit",
      header: "Credit",
      cell: (info) => <span className="flex items-center gap-x-2">
        <CheckboxInput
          label={null}
          register={register}
          registerName={`credit-${info.row.original.id}`}
          onChange={(value: any) => {
            console.log("Credit changed for item", info.row.original.id, "to", value);
            if (value) {
              setValue(`amount-${info.row.original.id}`, info.row.original.unitPrice * info.row.original.quantity);
              setValue(`quantity-${info.row.original.id}`, info.row.original.quantity);
            } else {
              setValue(`amount-${info.row.original.id}`, 0);
              setValue(`quantity-${info.row.original.id}`, 0);
            }

            setValue(`credit-${info.row.original.id}`, value);
          }}
          watch={watch}
          className="h-6 w-6"
        />
      </span>,
    }),

    colHelper.accessor("quantity", {
      id: "quantity",
      header: "Quantity",
      enableSorting: true,
      cell: (info) => <span className="flex items-center gap-x-2">
        <input
          type="number"
          {...register(`quantity-${info.row.original.id}`)}
          className="w-16"
          min={0}
          onClick={(e) => e.stopPropagation()}
        />
      </span>,
    }),

    colHelper.display({
      id: "amount",
      header: "Amount",
      enableSorting: false,
      cell: (info) => <span className="flex items-center gap-x-2">
        £<input
          type="number"
          {...register(`amount-${info.row.original.id}`)}
          className="w-24"
          min={0}
          onClick={(e) => e.stopPropagation()}
        />
      </span>,
    }),

    colHelper.display({
      id: "return",
      header: "Return",
      cell: (info) => <span className="flex items-center gap-x-2">
        <CheckboxInput
          label={null}
          register={register}
          registerName={`return-${info.row.original.id}`}
          onChange={(value: any) => {

          }}
          watch={watch}
          className="h-6 w-6"
        />
      </span>,
    }),

    colHelper.display({
      id: "scrap",
      header: "Scrap",
      cell: (info) => <span className="flex items-center gap-x-2">
        <CheckboxInput
          label={null}
          register={register}
          registerName={`scrap-${info.row.original.id}`}
          onChange={(value: any) => {

          }}
          watch={watch}
          className="h-6 w-6"
        />
      </span>,
    }),
  ];

  const table = useReactTable({
    data: data,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="mt-4 bg-white overflow-auto">
      <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="bg-red-200 text-gray-800">
              {headerGroup.headers.map((header, i) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                >
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: " ▲",
                        desc: " ▼",
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, i) => {
            let rowCells = row.getVisibleCells();
            return (
              <Fragment key={i}>
                <tr
                  key={row.id}
                  // onClick={() => {
                  //   if (row.original.partId) {
                  //     openInNewTab(`/stock/${row.original.partId}`);
                  //   }
                  // }}
                  className={classNames(
                    "hover:bg-gray-100",
                    row.original.partId
                      ? "cursor-pointer border-l-red-500 border-l-4"
                      : "",
                    watch(`credit-${row.original.id}`)
                      ? "bg-red-100 hover:bg-red-200"
                      : ""
                  )}
                >
                  {rowCells.map((cell) => (
                    <td key={cell.id} className="px-3 py-3 border border-red-100 text-sm">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <input type="hidden" {...register("itemTotal")} />
      {/* <small className="text-gray-500 mt-2">
        Credit Total: {PriceFormat(Object.values(creditTotals).reduce((a, b) => a + b, 0))}
      </small> */}
    </div>
  )
}